import { useEffect, useState } from 'react'
import AdminLayout from '../../components/shell/AdminLayout'
import EmptyState from '../../components/shell/EmptyState'
import AccountForm from '../../components/admin/AccountForm'
import AccountEditForm from '../../components/admin/AccountEditForm'
import { supabase } from '../../lib/supabase'
import {
  ACCOUNT_ROLES,
  createAccount,
  createTeam,
  getAccounts,
  getOpenTeams,
  roleLabel,
  setAccountActive,
  updateAccount,
  type Account,
  type AccountRole,
  type AccountTeam,
  type NewAccount,
  type TeamResult,
} from './accountStore'

type RoleFilter = 'all' | AccountRole
type StatusFilter = 'all' | 'active' | 'inactive'

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Deactivated' },
]

/**
 * docs/product/personas.md's Administrator: create and deactivate accounts,
 * and assign the four roles. A searchable list of every account with its
 * role and status; creating, editing a name or role, and deactivating or
 * reactivating each go through their own function, which checks the caller is
 * an active administrator and audits the change.
 */
export default function AdminAccountsPage() {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [teams, setTeams] = useState<AccountTeam[]>([])
  const [loaded, setLoaded] = useState(false)
  const [selfId, setSelfId] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  async function reload() {
    setAccounts(await getAccounts())
  }

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return
      setSelfId(session?.user.id ?? null)
      const [loadedAccounts, loadedTeams] = await Promise.all([getAccounts(), getOpenTeams()])
      if (cancelled) return
      setAccounts(loadedAccounts)
      setTeams(loadedTeams)
      setLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const query = search.trim().toLowerCase()
  const filtered = accounts.filter((account) => {
    if (roleFilter !== 'all' && account.role !== roleFilter) return false
    if (statusFilter === 'active' && !account.active) return false
    if (statusFilter === 'inactive' && account.active) return false
    if (!query) return true
    return `${account.firstName} ${account.lastName} ${account.email} ${roleLabel(account.role)}`.toLowerCase().includes(query)
  })

  const editing = accounts.find((account) => account.id === editingId) ?? null
  const deactivating = accounts.find((account) => account.id === deactivatingId) ?? null
  const releasedTeams = deactivating ? teams.filter((team) => team.supervisorId === deactivating.id) : []

  function closeForms() {
    setCreating(false)
    setEditingId(null)
    setDeactivatingId(null)
    setReason('')
    setFormError(null)
  }

  async function addTeam(name: string): Promise<TeamResult> {
    const result = await createTeam(name, teams.find((team) => team.fieldOfficeId)?.fieldOfficeId)
    if (result.ok) setTeams((current) => [...current, result.team].sort((a, b) => a.name.localeCompare(b.name)))
    return result
  }

  async function saveNew(account: NewAccount) {
    setSaving(true)
    setFormError(null)
    const result = await createAccount(account)
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    await Promise.all([reload(), getOpenTeams().then(setTeams)])
    closeForms()
    setNotice(`Created the account for ${account.firstName} ${account.lastName}.`)
  }

  async function saveEdit(values: { firstName: string; lastName: string; role: AccountRole }) {
    if (!editing) return
    setSaving(true)
    setFormError(null)
    const result = await updateAccount(editing.id, values)
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    await reload()
    closeForms()
    setNotice(`Updated the account for ${values.firstName} ${values.lastName}.`)
  }

  async function changeActive(account: Account, active: boolean, why?: string) {
    setSaving(true)
    setFormError(null)
    const result = await setAccountActive(account.id, active, why)
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    await reload()
    closeForms()
    setNotice(`${active ? 'Reactivated' : 'Deactivated'} the account for ${account.firstName} ${account.lastName}.`)
  }

  return (
    <AdminLayout current="accounts" title="Accounts & roles" subtitle="Create accounts, assign roles, and deactivate access">
      <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
        <label htmlFor="account-search" className="sr-only">
          Search accounts
        </label>
        <input
          id="account-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name, email, or role"
          className="min-h-11 min-w-[240px] flex-1 rounded-control border border-line-strong px-3 text-small"
        />
        <div className="flex items-center gap-2">
          <label htmlFor="account-role-filter" className="text-small font-bold text-ink-muted">
            Role
          </label>
          <select
            id="account-role-filter"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value as RoleFilter)}
            className="min-h-11 rounded-control border border-line-strong px-2.5 text-small"
          >
            <option value="all">All roles</option>
            {ACCOUNT_ROLES.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div role="group" aria-label="Filter by status" className="flex gap-2">
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={statusFilter === option.key}
              onClick={() => setStatusFilter(option.key)}
              className={`flex min-h-11 items-center justify-center rounded-control border px-3 text-small font-bold ${
                statusFilter === option.key ? 'border-primary bg-selected' : 'border-line-strong hover:bg-surface-2'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-expanded={creating}
          onClick={() => {
            closeForms()
            setNotice(null)
            setCreating(true)
          }}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
        >
          Create account
        </button>
      </div>

      {notice && (
        <p role="status" className="text-small font-semibold text-success">
          {notice}
        </p>
      )}

      {creating && <AccountForm teams={teams} saving={saving} error={formError} onSave={saveNew} onCancel={closeForms} onCreateTeam={addTeam} />}

      {editing && (
        <AccountEditForm
          key={editing.id}
          account={editing}
          isSelf={editing.id === selfId}
          saving={saving}
          error={formError}
          onSave={saveEdit}
          onCancel={closeForms}
        />
      )}

      {deactivating && (
        <form
          className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
          aria-label={`Deactivate ${deactivating.firstName} ${deactivating.lastName}`}
          onSubmit={(event) => {
            event.preventDefault()
            if (!saving) void changeActive(deactivating, false, reason.trim() || undefined)
          }}
        >
          <p className="text-small">
            Deactivating <span className="font-bold">{deactivating.firstName} {deactivating.lastName}</span> ends their access on their next request and stops them signing in. Their records stay.
            {releasedTeams.length > 0 &&
              ` ${releasedTeams.length === 1 ? 'The team they lead, ' : 'The teams they lead, '}${releasedTeams.map((team) => team.name).join(', ')}, will have no supervisor until an administrator assigns one.`}
          </p>
          <div>
            <label htmlFor="deactivate-reason" className="mb-1 block text-caption font-bold text-ink-muted">
              Reason (optional)
            </label>
            <input
              id="deactivate-reason"
              type="text"
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
            />
          </div>
          {formError && (
            <p role="alert" className="text-caption font-semibold text-error">
              {formError}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
            >
              {saving ? 'Deactivating…' : 'Deactivate account'}
            </button>
            <button
              type="button"
              onClick={closeForms}
              className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {!loaded ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : filtered.length === 0 ? (
        <EmptyState>No accounts match your search.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-surface border border-line">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Name', 'Email', 'Role', 'Status'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide text-ink-subtle uppercase"
                  >
                    {heading}
                  </th>
                ))}
                <th scope="col" className="border-b border-line bg-surface-2 px-3 py-2 text-left">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((account) => {
                const name = `${account.firstName} ${account.lastName}`
                const isSelf = account.id === selfId
                return (
                  <tr key={account.id}>
                    <th scope="row" className="border-b border-line px-3 py-2 text-left text-small font-bold">
                      {name}
                    </th>
                    <td className="border-b border-line px-3 py-2 text-small text-ink-muted">{account.email}</td>
                    <td className="border-b border-line px-3 py-2 text-small">{roleLabel(account.role)}</td>
                    <td className="border-b border-line px-3 py-2 text-small">
                      <span
                        className={`rounded-full px-2 py-0.5 text-caption font-bold ${
                          account.active ? 'bg-success/10 text-success' : 'bg-surface-2 text-ink-muted'
                        }`}
                      >
                        {account.active ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                    <td className="border-b border-line px-3 py-2 text-small">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          aria-label={`Edit ${name}`}
                          onClick={() => {
                            closeForms()
                            setNotice(null)
                            setEditingId(account.id)
                          }}
                          className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2"
                        >
                          Edit
                        </button>
                        {account.active ? (
                          !isSelf && (
                            <button
                              type="button"
                              aria-label={`Deactivate ${name}`}
                              onClick={() => {
                                closeForms()
                                setNotice(null)
                                setDeactivatingId(account.id)
                              }}
                              className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2"
                            >
                              Deactivate
                            </button>
                          )
                        ) : (
                          <button
                            type="button"
                            aria-label={`Reactivate ${name}`}
                            disabled={saving}
                            onClick={() => {
                              setNotice(null)
                              void changeActive(account, true)
                            }}
                            className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2 disabled:opacity-40"
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}
